import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Plus,
  ChevronDown,
  ChevronRight,
  GripVertical,
  Trash2,
  Copy,
  Eye as EyeIcon,
  Clock,
  MoreVertical,
  Play,
  HelpCircle,
  FileText,
  Layers,
  AlignLeft,
  Move,
  GitMerge,
  Image,
  Headphones,
  Download,
  Edit3,
  Type,
  Edit,
  Check,
  X,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const ELEMENT_ICONS = {
  video: Play,
  quiz: HelpCircle,
  case_study: FileText,
  flashcard: Layers,
  fill_gaps: AlignLeft,
  drag_drop_task: Move,
  matching: GitMerge,
  image_hotspot: Image,
  audio: Headphones,
  downloadable: Download,
  reflection_journal: Edit3,
  text: Type,
};

const ELEMENT_COLORS = {
  video: 'bg-red-100 text-red-700',
  quiz: 'bg-purple-100 text-purple-700',
  case_study: 'bg-blue-100 text-blue-700',
  flashcard: 'bg-amber-100 text-amber-700',
  fill_gaps: 'bg-green-100 text-green-700',
  drag_drop_task: 'bg-cyan-100 text-cyan-700',
  matching: 'bg-pink-100 text-pink-700',
  image_hotspot: 'bg-indigo-100 text-indigo-700',
  audio: 'bg-orange-100 text-orange-700',
  downloadable: 'bg-slate-100 text-slate-700',
  reflection_journal: 'bg-teal-100 text-teal-700',
  text: 'bg-gray-100 text-gray-700',
};

// Sortable Module Component
const SortableModule = ({
  module,
  isSelected,
  selectedElement,
  onSelect,
  onSelectElement,
  onUpdate,
  onDelete,
  onAddElement,
  onDeleteElement,
  onDuplicateModule,
  onDuplicateElement,
  onPreviewElement,
  onEditDelay,
  previewMode,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(module.title);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: module.id,
    data: { type: 'module' },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleSaveTitle = () => {
    if (editTitle.trim() && editTitle !== module.title) {
      onUpdate(module.id, { title: editTitle.trim() });
    }
    setIsEditing(false);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'border rounded-xl mb-3 transition-all',
        isDragging ? 'opacity-50' : '',
        isSelected ? 'border-[#20acf6] ring-2 ring-[#20acf6]/20' : 'border-slate-200'
      )}
    >
      {/* Module Header */}
      <div
        className={cn(
          'flex items-center gap-2 p-3 rounded-t-xl cursor-pointer',
          isSelected ? 'bg-[#20acf6]/5' : 'bg-slate-50 hover:bg-slate-100'
        )}
        onClick={() => onSelect(module)}
      >
        <button
          {...attributes}
          {...listeners}
          className="p-1 hover:bg-slate-200 rounded cursor-grab"
        >
          <GripVertical className="h-4 w-4 text-slate-400" />
        </button>

        <button
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
          className="p-1 hover:bg-slate-200 rounded"
        >
          {isExpanded ? (
            <ChevronDown className="h-4 w-4 text-slate-500" />
          ) : (
            <ChevronRight className="h-4 w-4 text-slate-500" />
          )}
        </button>

        {isEditing ? (
          <div className="flex-1 flex items-center gap-2">
            <Input
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              className="h-8 text-sm"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveTitle();
                if (e.key === 'Escape') setIsEditing(false);
              }}
              onClick={(e) => e.stopPropagation()}
            />
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={handleSaveTitle}>
              <Check className="h-4 w-4 text-green-600" />
            </Button>
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setIsEditing(false)}>
              <X className="h-4 w-4 text-red-600" />
            </Button>
          </div>
        ) : (
          <>
            <span className="flex-1 font-medium text-sm text-slate-900">
              {module.title}
            </span>
            {module.is_free_preview && (
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#10B981]/15 text-[#10B981]" data-testid={`free-preview-badge-${module.id}`}>
                Free Preview
              </span>
            )}
            {module.unlock_after_days > 0 && (
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 inline-flex items-center gap-1" data-testid={`module-delay-badge-${module.id}`}>
                <Clock className="h-2.5 w-2.5" /> Unlocks day {module.unlock_after_days}
              </span>
            )}
            <span className="text-xs text-slate-500">
              {module.elements?.length || 0} elements
            </span>
          </>
        )}

        {!isEditing && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setIsEditing(true)}>
                <Edit className="h-4 w-4 mr-2" />
                Rename
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onUpdate(module.id, { is_free_preview: !module.is_free_preview })}>
                <Play className="h-4 w-4 mr-2" />
                {module.is_free_preview ? 'Remove Free Preview' : 'Make Free Preview'}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onDuplicateModule(module.id)} data-testid={`duplicate-module-${module.id}`}>
                <Copy className="h-4 w-4 mr-2" />
                Duplicate module
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onEditDelay(module)} data-testid={`module-delay-${module.id}`}>
                <Clock className="h-4 w-4 mr-2" />
                Unlock delay…
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => onDelete(module.id)}
                className="text-red-600"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Module Elements */}
      {isExpanded && (
        <div className="p-3 pt-0">
          <SortableContext
            items={module.elements?.map(e => e.id) || []}
            strategy={verticalListSortingStrategy}
          >
            {module.elements?.length > 0 ? (
              <div className="space-y-2 mt-2">
                {module.elements.map((element) => (
                  <SortableElement
                    key={element.id}
                    element={element}
                    moduleId={module.id}
                    isSelected={selectedElement?.id === element.id}
                    onSelect={() => onSelectElement(element, module)}
                    onDelete={() => onDeleteElement(module.id, element.id)}
                    onDuplicate={() => onDuplicateElement(module.id, element.id)}
                    onPreview={() => onPreviewElement(element)}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-sm text-slate-500">
                No elements yet. Add one from the sidebar.
              </div>
            )}
          </SortableContext>

          {!previewMode && (
            <Button
              variant="outline"
              size="sm"
              className="w-full mt-3 border-dashed"
              onClick={() => onSelect(module)}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Element
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

// Sortable Element Component
const SortableElement = ({
  element,
  moduleId,
  isSelected,
  onSelect,
  onDelete,
  onDuplicate,
  onPreview,
}) => {
  const Icon = ELEMENT_ICONS[element.type] || FileText;
  const colorClass = ELEMENT_COLORS[element.type] || 'bg-slate-100 text-slate-700';

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: element.id,
    data: { type: 'element', moduleId },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-all',
        isDragging ? 'opacity-50' : '',
        isSelected
          ? 'border-[#20acf6] bg-[#20acf6]/5'
          : 'border-slate-200 hover:border-slate-300 bg-white'
      )}
      onClick={onSelect}
    >
      <button
        {...attributes}
        {...listeners}
        className="p-1 hover:bg-slate-100 rounded cursor-grab"
        onClick={(e) => e.stopPropagation()}
      >
        <GripVertical className="h-3 w-3 text-slate-400" />
      </button>

      <div className={cn('w-7 h-7 rounded flex items-center justify-center', colorClass)}>
        <Icon className="h-4 w-4" />
      </div>

      <div className="flex-1 min-w-0">
        <span className="text-sm font-medium text-slate-900 truncate block">
          {element.title}
        </span>
      </div>

      <Button variant="ghost" size="icon" className="h-7 w-7" title="Preview as learner" aria-label="Preview element"
        onClick={(e) => { e.stopPropagation(); onPreview(); }} data-testid={`preview-element-${element.id}`}>
        <EyeIcon className="h-3 w-3" />
      </Button>
      <Button variant="ghost" size="icon" className="h-7 w-7" title="Duplicate" aria-label="Duplicate element"
        onClick={(e) => { e.stopPropagation(); onDuplicate(); }} data-testid={`duplicate-element-${element.id}`}>
        <Copy className="h-3 w-3" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7 hover:bg-red-100 hover:text-red-600"
        aria-label="Delete element"
        data-testid={`delete-element-${element.id}`}
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
      >
        <Trash2 className="h-3 w-3" />
      </Button>
    </div>
  );
};

// Main Canvas Component
const ModuleCanvas = ({
  course,
  selectedModule,
  selectedElement,
  onSelectModule,
  onSelectElement,
  onAddModule,
  onImportModule,
  onDeleteModule,
  onUpdateModule,
  onAddElement,
  onDeleteElement,
  onDuplicateModule,
  onDuplicateElement,
  onPreviewElement,
  onEditDelay,
  previewMode,
}) => {
  return (
    <div className="flex-1 bg-slate-100 overflow-hidden" data-testid="module-canvas">
      <ScrollArea className="h-full">
        <div className="p-6 max-w-3xl mx-auto">
          {/* Course Title */}
          <div className="mb-6">
            <h2 className="text-xl font-bold text-[#2a3e5d]">{course?.title}</h2>
            <p className="text-sm text-slate-600 mt-1">
              {course?.modules?.length || 0} modules
            </p>
          </div>

          {/* Modules */}
          <SortableContext
            items={course?.modules?.map(m => m.id) || []}
            strategy={verticalListSortingStrategy}
          >
            {course?.modules?.map((module) => (
              <SortableModule
                key={module.id}
                module={module}
                isSelected={selectedModule?.id === module.id}
                selectedElement={selectedElement}
                onSelect={onSelectModule}
                onSelectElement={onSelectElement}
                onUpdate={onUpdateModule}
                onDelete={onDeleteModule}
                onAddElement={onAddElement}
                onDeleteElement={onDeleteElement}
                onDuplicateModule={onDuplicateModule}
                onDuplicateElement={onDuplicateElement}
                onPreviewElement={onPreviewElement}
                onEditDelay={onEditDelay}
                previewMode={previewMode}
              />
            ))}
          </SortableContext>

          {/* Add Module Button */}
          {!previewMode && course?.id && (
            <div className="grid sm:grid-cols-2 gap-3">
              <Button
                variant="outline"
                className="w-full border-dashed border-2 py-8"
                onClick={onAddModule}
                data-testid="add-module-btn"
              >
                <Plus className="h-5 w-5 mr-2" />
                Add Module
              </Button>
              <Button
                variant="outline"
                className="w-full border-dashed border-2 py-8"
                onClick={onImportModule}
                data-testid="import-module-open-btn"
              >
                <Copy className="h-5 w-5 mr-2" />
                Import Module from another course
              </Button>
            </div>
          )}

          {!course?.id && (
            <div className="text-center py-12 text-slate-500">
              <p>Save your course first to add modules</p>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};

export default ModuleCanvas;
